import { Component, OnInit, ViewChild } from '@angular/core';
import { GridApi } from 'ag-grid-community';
// Model
import { QueueModel, QueuesColumnModel } from '../manage-queues/model/queue.model';
import { QueuesService } from './service/queues.service';
import { every, lastValueFrom, Subject, takeUntil } from 'rxjs';
// Components
import { CustomQueueNameCellComponent } from './components/custom-cell/name/custom-name-cell.component';
import { CustomQueueActionCellComponent } from './components/custom-cell/action/custom-queues-action-cell.component';
import { PaginationModel } from '@cv-invision/cv.component-library.ux';
import { DialogService } from 'primeng/dynamicdialog';
import { QueueEditorDialogComponent } from './components/dialog/queue-editor/queue-editor.dialog.component';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogResult } from './enum/dialog.enum';
import { ResolveDialogComponent } from './components/dialog/resolve-dialog/resolve-dialog.component';

@Component({
    selector: 'cv-manage-queues',
    templateUrl: 'manage-queues.component.html',
    styleUrls: ['manage-queues.component.scss'],
})
export class ManageQueuesComponent implements OnInit {
    // Toolbar
    public searchText = '';
    // Grid
    @ViewChild('grid', { static: true }) agGrid: GridApi;
    public gridOptions: any;
    public selectedQueueRow: QueueModel;
    public pagination: PaginationModel = { totalCount: 0, offset: 0, limit: 10 };
    public queues: QueueModel[] = [];
    public queuesTmp: QueueModel[] = []; // keep only for local search, if you really need it
    private destroy$ = new Subject<void>();
    private atComponentDestroy = new Subject();

    constructor(
        private queueService: QueuesService,
        private dialogService: DialogService,
        private confirmationService: ConfirmationService,
        private messageService: MessageService, 
    ) {}

    ngOnInit() {
        this.loadQueues();
        this.initGridOptions();
        this.queueService.$refreshQueues.pipe(takeUntil(this.atComponentDestroy)).subscribe(() => {
            this.loadQueues();
        })
    }

    onInitializeGrid(params: any): void {
        this.agGrid = params.api;
        this.agGrid.sizeColumnsToFit();
    }

    /**
     * Inits the grid configurations.
     */
    private initGridOptions() {
        // grid options
        this.gridOptions = {
            rowHeight: 40,
            rowSelection: 'single',
            defaultColDef: {
                resizable: true,
            },
            localeText: {
                noRowsToShow: 'No Queues to show',
                loadingOoo: 'Loading Queues...',
            },
            columnDefs: new QueuesColumnModel().columns,
            frameworkComponents: {
                customActionCellComponent: CustomQueueActionCellComponent,
                customNameCellComponent: CustomQueueNameCellComponent,
            },
        };
    }

    loadQueues() {
        this.queueService
          .getQueues(this.pagination, { name: this.searchText })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (res) => {
              this.queues = res.data;
      
              const total = this.pagination.totalCount = res.totalCount ?? res.data.length ?? 0;
      
              if (total === 0) {
                this.pagination.offset = 0;
                return;
              }
              const maxOffset = Math.floor((total - 1) / this.pagination.limit) * this.pagination.limit;
              if (this.pagination.offset > maxOffset) {
                this.pagination.offset = Math.max(0, maxOffset);
                this.loadQueues(); // single follow-up call, no loop
              }
            },
            error: console.error,
          });
      }

    /**
     * Searches the queue by the last searched text
     * @param {string} search 
     */
    updateSearch(search: string) {
        this.searchText = search;
        this.loadQueues();
    }

    /**
     * Every time the view port has change, we ensure the columns fits properly
     */
    resizeToFitColumns(): void {
        this.agGrid.sizeColumnsToFit();
    }

    onRowDataChanged($event: any): void {
        if ($event.api && this.selectedQueueRow && this.queues.length) {
            $event.api.forEachNode((node) =>
                node.data.id === this.selectedQueueRow.id ? node.setSelected(true) : 0,
            );
        }
    }

    /**
     * Handles the pagination change of the table
     * @param p - The pagination object
     */
    onPaginationChange(p: PaginationModel): void {
        this.pagination = {
            ...this.pagination,
            offset: p.offset,
            limit: p.limit ?? this.pagination.limit,
        };
        this.loadQueues();
    }

    /**
     * Opens the dialog to trigger resolvers for queues
     */
    openResolveDialog() {
        this.dialogService
        .open(ResolveDialogComponent, {
            header: 'Resolve Queues',
            width: '950px',
            styleClass: 'queue-editor-dialog',
        })
    }

    /**
     * Handles the process of creating a new queue by opening a dialog.
     */
    newQueue() {
        this.dialogService
            .open(QueueEditorDialogComponent, {
                header: 'New Queue',
                width: '950px',
                data: {
                    filters: '',
                },
                styleClass: 'queue-editor-dialog',
            })
            .onClose.subscribe(async (dialogFilterResult: any): Promise<void> => {
                if (
                    dialogFilterResult === DialogResult.SUCCESS
                ) {
                   this.loadQueues();
                }
            });
    }
    
    /**
	 * Close all when the element is destroyed
	 */
	ngOnDestroy(): void {
		// Globally Destroy anything attached to it
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
